import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation, useParams, Navigate } from "react-router-dom";
import { LANGS } from "../content/shared";
import { DICTS, LangContext } from "../i18n";
import Header from "./Header";
import Footer from "./Footer";
import CookieBanner, { getConsent, loadOptionalScripts } from "./CookieBanner";
import { Seo } from "./Seo";

const scrollToTop = (lenis) => {
  if (lenis) lenis.scrollTo(0, { immediate: true, force: true });
  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const useLenis = (pathname) => {
  const lenisRef = useRef(null);
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let lenis;
    let raf;
    let alive = true;
    import("lenis").then(({ default: Lenis }) => {
      if (!alive) return;
      lenis = new Lenis({ duration: 1.1, smoothWheel: true, stopInertiaOnNavigate: true });
      lenisRef.current = lenis;
      const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    });
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      lenisRef.current = null;
      lenis?.destroy();
    };
  }, []);
  useLayoutEffect(() => {
    scrollToTop(lenisRef.current);
    const id = requestAnimationFrame(() => scrollToTop(lenisRef.current));
    return () => cancelAnimationFrame(id);
  }, [pathname]);
};

export default function Layout() {
  const { lang } = useParams();
  const { pathname } = useLocation();
  const [cookieOpen, setCookieOpen] = useState(false);
  useLenis(pathname);
  useEffect(() => {
    const c = getConsent();
    if (!c) setCookieOpen(true);
    if (c === "accepted") loadOptionalScripts();
  }, []);
  if (!LANGS.includes(lang)) {
    const rest = pathname.split("/").slice(2).join("/");
    return <Navigate to={`/en/${rest}`} replace />;
  }
  const rest = pathname.split("/").slice(2).filter(Boolean).join("/");
  return (
    <LangContext.Provider value={{ lang, t: DICTS[lang] }}>
      <Seo lang={lang} rest={rest} />
      <div className="flex min-h-screen flex-col" lang={lang}>
        <Header />
        <main id="main" className="flex-1">
          <Outlet />
        </main>
        <Footer onCookies={() => setCookieOpen(true)} />
      </div>
      <CookieBanner open={cookieOpen} onClose={() => setCookieOpen(false)} />
    </LangContext.Provider>
  );
}
